// Send Logs on a Mac's link (docs/features/send-logs.md › The wire — kind `logs`, 0x38 "8"): a tap makes the bundle,
// keeps it on disk for that Mac (outbox.js) and sends every piece as {t: logsPart, …}; the Mac's `logsAck` settles it —
// ok: forgotten here, "Sent to <Mac>"; tooSoon: tried again by itself after `retryIn`; any other code: kept, "Not sent",
// until a new Send replaces it. No link, or a link that drops before the ack: kept, and sent when that Mac's link comes
// up again. The Mac's `logsAsk` only shows the ask card (index.js): nothing is sent without the person's tap.
import { bundleID, compress, sha256Hex, pieces, toBase64, fromBase64 } from './bundle.js'
import { openOutbox } from './outbox.js'
import { macShortName } from '../core/macshortname.js'

/// The clock and timers, replaced by the tests.
export const clock = { now: () => Date.now(), setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (t) => clearTimeout(t) }
export const ACK_WAIT = 60_000   // no answer this long (an older Mac app, a link gone quiet): kept for the next link

const REASONS = {
  notPaired: (name) => `this browser isn't paired with ${name}`,
  tooLarge: () => 'too large',
  damaged: () => 'damaged on the way',
  failed: () => "the Mac couldn't save it",
}

const links = new Map()     // Mac key → { mac, link }
const shown = new Map()     // Mac key → { name, state, reason, quiet } — what its row says (sent lives only here)
const timers = new Map()    // Mac key → a retry or an ack wait
const listeners = new Set()
let outbox = null, loading = null

const short = (name) => macShortName(name) || String(name || '') || 'the Mac'
const tell = () => { for (const fn of [...listeners]) { try { fn() } catch {} } }
const box = () => (outbox ||= openOutbox())

/// What every kept bundle says, read once from the disk (a page reloaded mid-send: kept).
function ready() {
  loading ||= box().then((o) => o.all()).then((records) => {
    for (const r of records) if (!shown.has(r.mac)) shown.set(r.mac, { name: r.name, state: r.state === 'sending' ? 'kept' : r.state, reason: r.reason || '' })
    tell()
  }).catch(() => {})
  return loading
}

function up(key) {
  const e = links.get(key)
  if (!e) return false
  const rs = e.link.channel?.readyState
  return e.mac.windows === e.link && !['offline', 'paused'].includes(e.mac.link) && rs !== 'closing' && rs !== 'closed'
}

function set(key, fields) {
  const was = shown.get(key) || {}
  shown.set(key, { ...was, quiet: false, ...fields })
  tell()
}

function stopTimer(key) { const t = timers.get(key); if (t != null) clock.clearTimeout(t); timers.delete(key) }

async function save(record, fields) {
  Object.assign(record, fields)
  await (await box()).put(record)
}

/// Sends what is kept for that Mac if its link is up and nothing holds it back.
async function push(key) {
  const o = await box()
  const record = await o.get(key)
  if (!record || record.state === 'refused') return
  stopTimer(key)
  if (record.state === 'waiting' && record.retryAt > clock.now()) {
    timers.set(key, clock.setTimeout(() => { timers.delete(key); push(key) }, record.retryAt - clock.now()))
    set(key, { name: record.name, state: 'waiting' })
    return
  }
  if (!up(key)) { await save(record, { state: 'kept' }); set(key, { name: record.name, state: 'kept' }); return }
  const { link } = links.get(key)
  await save(record, { state: 'sending' })
  set(key, { name: record.name, state: 'sending' })
  for (const piece of pieces(record.id, fromBase64(record.data), record.sha256)) {
    const { id, part, parts, bytes, sha256, data } = piece
    link.send('logs', { t: 'logsPart', id, part, parts, bytes, sha256, data })
  }
  const id = record.id
  timers.set(key, clock.setTimeout(async () => {
    timers.delete(key)
    const now = await o.get(key)
    if (now?.id === id && now.state === 'sending') { await save(now, { state: 'kept' }); set(key, { state: 'kept', quiet: true }) }
  }, ACK_WAIT))
}

export const SendLogs = {
  /// A Mac's link is up (index.js's watchSendLogs): what is kept for it goes now.
  attach(mac, link) {
    links.set(mac.key, { mac, link })
    link.sendLogsKey = mac.key
    link.channel?.addEventListener?.('close', () => SendLogs.down(mac.key, link))
    tell()
    return ready().then(() => push(mac.key))
  },

  /// That link closed: a bundle on its way is kept for the next one.
  down(key, link) {
    if (links.get(key)?.link !== link) return
    links.delete(key)
    stopTimer(key)
    if (shown.get(key)?.state === 'sending') set(key, { state: 'kept' })
    else tell()
  },

  /// A tap: `make(asked)` gives the bundle's text; kept for that Mac (replacing an unsent one), then sent if it can be.
  async send(key, make, { asked = false, name } = {}) {
    await ready()
    name = short(name || links.get(key)?.mac?.name || shown.get(key)?.name)
    const text = await make(asked)
    const bytes = await compress(text)
    const record = { mac: key, name, id: bundleID(), data: toBase64(bytes), bytes: bytes.length, sha256: await sha256Hex(bytes), made: new Date(clock.now()).toISOString(), state: 'kept', reason: '' }
    stopTimer(key)
    await (await box()).put(record)
    set(key, { name, state: 'kept', reason: '' })
    await push(key)
    return record.id
  },

  /// A `logs` message from a Mac (the wire handler in index.js).
  async hear(link, message) {
    const key = link?.sendLogsKey
    if (!key || !message) return
    if (message.t === 'logsAsk') { SendLogs.onAsk(key, message); return }
    if (message.t !== 'logsAck') return
    const o = await box()
    const record = await o.get(key)
    if (!record || record.id !== message.id) return
    stopTimer(key)
    if (message.ok) { await o.del(key); set(key, { name: record.name, state: 'sent', reason: '' }); return }
    if (message.code === 'tooSoon') {
      await save(record, { state: 'waiting', retryAt: clock.now() + Math.max(1, Number(message.retryIn) || 60) * 1000 })
      return push(key)
    }
    const reason = REASONS[message.code]?.(record.name) || String(message.reason || message.code || 'refused')
    await save(record, { state: 'refused', reason })
    set(key, { name: record.name, state: 'refused', reason })
  },

  /// Reads the kept bundles from the disk (their rows say so), once.
  load: () => ready(),

  /// The ask card's hook (index.js).
  onAsk: () => {},

  /// The ⋯ sheet's rows: every Mac with a link up or something to say. [{ key, name, linked, state, line }]
  rows() {
    const keys = new Set([...[...links.keys()].filter(up), ...shown.keys()])
    return [...keys].map((key) => {
      const s = shown.get(key) || {}
      const name = short(links.get(key)?.mac?.name || s.name)
      return { key, name, linked: up(key), state: s.state || '', line: lineOf(s, name) }
    })
  },

  on(fn) { listeners.add(fn); return () => listeners.delete(fn) },

  /// Tests: forget what is held in memory (a reload), and use `store` as the disk; the hooks stay.
  reset(store = null) {
    for (const key of [...timers.keys()]) stopTimer(key)
    links.clear(); shown.clear()
    outbox = store ? Promise.resolve(store) : null
    loading = null
  },
}

/// The row's one line, for a short Mac name.
export function lineOf(s, name) {
  switch (s.state) {
    case 'kept': return s.quiet ? `Kept — ${name} didn't answer; sends when it reconnects` : `Kept — sends when ${name} connects`
    case 'sending': return 'Sending…'
    case 'sent': return `Sent to ${name}`
    case 'waiting': return `Waiting — ${name} takes one a minute`
    case 'refused': return `Not sent: ${s.reason}`
    default: return ''
  }
}
